// Polyfills must load before any other module runs (CSN-1324).
import "./lib/polyfills";
// Sentry must init before ./router (which wraps createBrowserRouter) is imported.
import "./instrument";

import { registerSW } from "virtual:pwa-register";
import {
  QueryCache,
  QueryClient,
  focusManager,
  hashKey
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import * as React from "react";
import { FlagProvider } from "@unleash/proxy-client-react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { UnleashClient } from "unleash-proxy-client";

import { isApiError } from "@repo/system";
import { T } from "@repo/transifex";

import { useFirebaseAuthState } from "./lib/firebase/hooks/use-firebase-auth-state";
import { AppProvider } from "./providers/app";
import {
  authActions,
  getAuth,
  startFirebaseAuthSync
} from "./providers/store/auth";
import { router } from "./router";
import { sessionsKeys } from "./api/sessions/keys";
import { tillsKeys } from "./api/tills/keys";
import {
  UNLEASH_ENVIRONMENT,
  UNLEASH_FRONTEND_API_TOKEN,
  UNLEASH_FRONTEND_API_URL
} from "./utils/env";
import { ScreenLoader } from "./components/screen/screen-loader";
import "./index.css";
import { logger } from "./services/logger";
import { createIDBPersister } from "./utils/persister";
import { isRetryableRequestError } from "./api/error";

// Import Web Till API for debug helpers
import "./services/web-till-api";

// Ensure all pending logs are sent before the app closes
window.addEventListener("beforeunload", () => logger.flush());

startFirebaseAuthSync();

const IDB_KEY = "POS-IDB";

// Tablet PWAs don't reliably fire `focus` on wake, so refetch on
// `visibilitychange` too — otherwise the app keeps yesterday's stale till.
focusManager.setEventListener((handleFocus) => {
  const onChange = () => {
    const isFocused =
      document.visibilityState === "visible" && document.hasFocus();

    handleFocus(isFocused);
  };

  document.addEventListener("visibilitychange", onChange, false);
  window.addEventListener("focus", onChange, false);

  return () => {
    document.removeEventListener("visibilitychange", onChange);
    window.removeEventListener("focus", onChange);
  };
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 10, // 10 minutes
      staleTime: 1000 * 60, // 1 minute
      refetchOnReconnect: true,
      retry: (_, err) => isRetryableRequestError(err)
    }
  },
  queryCache: new QueryCache({
    onError: (err, query) => {
      if (!isApiError(err)) return;

      // if the user is not logged in, redirect to the login page
      if (err.status === 401) {
        logger.warn(
          `User logged out due to ${err.status} error: ${err.body.message}`
        );
        authActions.logout();
        return;
      }

      if (err.status === 404) {
        const [scope] = query.queryKey;
        if (scope !== tillsKeys.all[0] && scope !== sessionsKeys.all[0]) {
          return;
        }

        const { tillId } = getAuth();
        if (!tillId) return;

        const currentTillKey = tillsKeys.detail(tillId);
        // Avoid refetch loop on the till's own 404.
        if (query.queryHash === hashKey(currentTillKey)) {
          logger.warn(`Till ${tillId} not found server-side`, {
            till_id: tillId
          });
          return;
        }

        void queryClient.invalidateQueries({ queryKey: currentTillKey });
      }
    }
  })
});

registerSW({ immediate: true });

const persister = createIDBPersister(IDB_KEY);

// Handle Vite Load Error (white screen of death)
window.addEventListener("vite:preloadError", () => {
  logger.warn("Vite failed to load dynamic imports - reloading the page");
  window.location.reload();
});

const unleashClient = new UnleashClient({
  url: UNLEASH_FRONTEND_API_URL,
  clientKey: UNLEASH_FRONTEND_API_TOKEN,
  appName: "pos",
  environment: UNLEASH_ENVIRONMENT
});

/**
 * Gates route rendering on Firebase auth readiness
 */
const FirebaseAuthGate = () => {
  const { isLoading } = useFirebaseAuthState();

  if (isLoading) {
    return (
      <ScreenLoader className="h-screen">
        <ScreenLoader.Title>
          <T _str="Loading..." />
        </ScreenLoader.Title>
        <ScreenLoader.Text>
          <T _str="Authenticating" />
        </ScreenLoader.Text>
      </ScreenLoader>
    );
  }

  return <RouterProvider router={router} />;
};

const el = document.getElementById("root");
if (!el) {
  throw new Error("Could not find root element");
}

const root = createRoot(el);
root.render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister }}
    >
      <FlagProvider unleashClient={unleashClient}>
        <AppProvider>
          <FirebaseAuthGate />
        </AppProvider>
      </FlagProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </PersistQueryClientProvider>
  </React.StrictMode>
);
